import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import PageHeader from '../../components/PageHeader';
import styles from './Dashboard.module.css';

const STATUS_BADGE = {
  'Pending':    { bg: '#fff3cd', color: '#856404' },
  'Approved':   { bg: '#d1e7dd', color: '#0f5132' },
  'In Transit': { bg: '#cfe2ff', color: '#084298' },
  'On its way': { bg: '#cfe2ff', color: '#084298' },
  'Delivered':  { bg: '#d1e7dd', color: '#0f5132' },
  'Rejected':   { bg: '#f8d7da', color: '#842029' },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { bg: '#e9ecef', color: '#495057' };
  return (
    <span style={{
      background: s.bg, color: s.color,
      padding: '2px 10px', borderRadius: 20,
      fontSize: '0.72rem', fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

export default function AdminDashboard() {
  const [orders, setOrders]         = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [pendingTxs, setPendingTxs] = useState([]);
  const [error, setError]           = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const unsubOrders = onSnapshot(
      collection(db, 'orders'),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setOrders(all);
        const sorted = [...all].sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
        setRecentOrders(sorted.slice(0, 5));
        setLoading(false);
      },
      err => { console.error('[Dashboard] orders:', err); setError(err.message); setLoading(false); }
    );

    const unsubTx = onSnapshot(
      query(collection(db, 'transactions'), where('status', '==', 'Pending')),
      snap => setPendingTxs(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.error('[Dashboard] transactions:', err)
    );

    return () => { unsubOrders(); unsubTx(); };
  }, []);

  const stats = [
    { label: 'Total Orders',        value: orders.length,                                                                         sub: 'All time'               },
    { label: 'Pending Orders',       value: orders.filter(o => o.status === 'Pending').length,                                    sub: 'Awaiting approval'      },
    { label: 'In Transit',           value: orders.filter(o => o.status === 'In Transit' || o.status === 'On its way').length,    sub: 'In transit'             },
    { label: 'Delivered',            value: orders.filter(o => o.status === 'Delivered').length,                                  sub: 'Completed'              },
    { label: 'Pending Verification', value: pendingTxs.length,                                                                    sub: 'Transactions to verify' },
  ];

  if (error) return (
    <>
      <PageHeader title="Dashboard" description="Welcome back, Admin. Here's your system overview." />
      <div className={styles.panel}>
        <p style={{ color: 'red' }}>Firestore error: {error}</p>
      </div>
    </>
  );

  return (
    <>
      <PageHeader title="Dashboard" description="Welcome back, Admin. Here's your system overview." />

      {loading && <p style={{ padding: '1rem', color: 'var(--text-muted)' }}>Loading…</p>}

      <div className={styles.statsGrid}>
        {stats.map((s, i) => (
          <div className={styles.statCard} key={i}>
            <p className={styles.statLabel}>{s.label}</p>
            <p className={styles.statValue}>{s.value}</p>
            <p className={styles.statSub}>{s.sub}</p>
          </div>
        ))}
      </div>

      <div className={styles.panels}>
        {/* Recent Orders */}
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>Recent Orders</h3>
          {recentOrders.length === 0 ? (
            <p className={styles.empty}>No orders yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Branch</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map(o => (
                  <tr key={o.id}>
                    <td className={styles.mono}>#{o.id.slice(0, 8).toUpperCase()}</td>
                    <td>{o.branch || '—'}</td>
                    <td>{o.total != null ? `₱${Number(o.total).toLocaleString()}` : '—'}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td>{o.createdAt?.toDate?.().toLocaleDateString() ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pending Transactions */}
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>Pending Verifications</h3>
          {pendingTxs.length === 0 ? (
            <p className={styles.empty}>No pending transactions.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>TX ID</th>
                  <th>Branch</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {pendingTxs.slice(0, 5).map(t => (
                  <tr key={t.id}>
                    <td className={styles.mono}>#{t.id.slice(0, 8).toUpperCase()}</td>
                    <td>{t.branchName || t.branchId || '—'}</td>
                    <td>₱{Number(t.amount ?? 0).toLocaleString()}</td>
                    <td>{t.createdAt?.toDate?.().toLocaleDateString() ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
